import { useEffect, useCallback, useMemo } from "react";

interface PixelConfig {
  gaPixelId?: string | null;
  metaPixelId?: string | null;
}

// Strict regex patterns to validate pixel IDs and prevent XSS
const GA4_PATTERN = /^G-[A-Z0-9]{10,12}$/;
const UA_PATTERN = /^UA-[0-9]+-[0-9]+$/;
const META_PATTERN = /^[0-9]{15,16}$/;

function isValidGAPixelId(id: string | null | undefined): id is string {
  if (!id) return false;
  return GA4_PATTERN.test(id) || UA_PATTERN.test(id);
}

function isValidMetaPixelId(id: string | null | undefined): id is string {
  if (!id) return false;
  return META_PATTERN.test(id);
}

export function usePixelTracking(config: PixelConfig) {
  // Validate pixel IDs before use to prevent XSS injection
  const gaPixelId = useMemo(() => 
    isValidGAPixelId(config.gaPixelId) ? config.gaPixelId : null, 
    [config.gaPixelId]
  );
  const metaPixelId = useMemo(() => 
    isValidMetaPixelId(config.metaPixelId) ? config.metaPixelId : null, 
    [config.metaPixelId]
  );

  // Inject GA4 script
  useEffect(() => {
    if (!gaPixelId) return;

    // Check if already loaded
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${gaPixelId}"]`)) {
      return;
    }

    // Load gtag script
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaPixelId}`;
    document.head.appendChild(script);

    // Initialize gtag
    const inlineScript = document.createElement("script");
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaPixelId}');
    `;
    document.head.appendChild(inlineScript);

    console.log("[Pixel Tracking] GA4 initialized:", gaPixelId);

    return () => {
      // Cleanup on unmount (optional, scripts usually persist)
    };
  }, [gaPixelId]);

  // Inject Meta Pixel script
  useEffect(() => {
    if (!metaPixelId) return;

    // Check if already loaded
    if (window.fbq) {
      return;
    }

    // Initialize Meta Pixel
    const script = document.createElement("script");
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${metaPixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement("noscript");
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(noscript);

    console.log("[Pixel Tracking] Meta Pixel initialized:", metaPixelId);
  }, [metaPixelId]);

  // Track GA4 event
  const trackGAEvent = useCallback((eventName: string, params?: Record<string, unknown>) => {
    if (!gaPixelId || !window.gtag) return;
    
    window.gtag("event", eventName, params);
    console.log("[Pixel Tracking] GA4 event:", eventName, params);
  }, [gaPixelId]);

  // Track Meta Pixel event
  const trackMetaEvent = useCallback((eventName: string, params?: Record<string, unknown>) => {
    if (!metaPixelId || !window.fbq) return;
    
    window.fbq("track", eventName, params);
    console.log("[Pixel Tracking] Meta event:", eventName, params);
  }, [metaPixelId]);

  // Convenience method to track on both platforms
  const trackEvent = useCallback((eventName: string, params?: Record<string, unknown>) => {
    trackGAEvent(eventName, params);
    trackMetaEvent(eventName, params);
  }, [trackGAEvent, trackMetaEvent]);

  // Track form start
  const trackFormStart = useCallback(() => {
    trackGAEvent("form_start", { form_name: "lead_magnet" });
    trackMetaEvent("InitiateCheckout");
  }, [trackGAEvent, trackMetaEvent]);

  // Track form completion / lead
  const trackFormComplete = useCallback((email?: string) => {
    trackGAEvent("generate_lead", { email_provided: !!email });
    trackMetaEvent("Lead", { content_name: "form_completion" });
  }, [trackGAEvent, trackMetaEvent]);

  // Track PDF download
  const trackPdfDownload = useCallback(() => {
    trackGAEvent("file_download", { file_type: "pdf" });
    trackMetaEvent("Purchase", { content_name: "pdf_download" });
  }, [trackGAEvent, trackMetaEvent]);

  return {
    trackEvent,
    trackGAEvent,
    trackMetaEvent,
    trackFormStart,
    trackFormComplete,
    trackPdfDownload,
  };
}

// Type declarations for global objects
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}
